'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { Archive, getPublishedArchives } from '@/lib/db/archives';
import { BookMarked, MapPin, Users, ArrowRight, Loader2 } from 'lucide-react';

function ArchiveCard({ archive }: { archive: Archive }) {
  const publishedDate = archive.publishedAt
    ? new Date(archive.publishedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

  return (
    <Link
      href={`/archives/${archive.id}`}
      className="group flex flex-col rounded-xl border overflow-hidden transition-all duration-200 hover:-translate-y-0.5"
      style={{
        backgroundColor: 'var(--card)',
        borderColor: 'var(--border)',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--primary)';
        (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 4px 24px rgba(224,90,71,0.10)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--border)';
        (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
      }}
    >
      {/* Top accent bar */}
      <div className="h-1 w-full" style={{ backgroundColor: 'var(--primary)', opacity: 0.7 }} />

      <div className="flex flex-col flex-1 p-5 gap-3">
        {/* Title */}
        <h3
          className="text-base font-semibold leading-snug line-clamp-2 group-hover:text-amber-500 transition-colors duration-150"
          style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}
        >
          {archive.title}
        </h3>

        {/* Description */}
        <p className="text-sm line-clamp-3 flex-1" style={{ color: 'var(--muted-foreground)' }}>
          {archive.description}
        </p>

        {/* Meta row */}
        <div className="flex flex-wrap items-center gap-3 pt-1">
          {archive.region && (
            <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
              <MapPin size={11} />
              {archive.region}
            </span>
          )}
          <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <Users size={11} />
            {archive.contributionCount} contribution{archive.contributionCount !== 1 ? 's' : ''}
          </span>
          {publishedDate && (
            <span className="text-xs ml-auto" style={{ color: 'var(--muted-foreground)' }}>
              {publishedDate}
            </span>
          )}
        </div>

        {/* Creator */}
        <div className="flex items-center justify-between pt-1 border-t" style={{ borderColor: 'var(--border)' }}>
          <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
            by <span style={{ color: 'var(--foreground)' }}>{archive.creatorName}</span>
          </span>
          <ArrowRight
            size={14}
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-150"
            style={{ color: 'var(--primary)' }}
          />
        </div>
      </div>
    </Link>
  );
}

export default function LatestArchives() {
  const [archives, setArchives] = useState<Archive[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPublishedArchives(6)
      .then(setArchives)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (!loading && archives.length === 0) return null;

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--background)' }}>
      <div className="max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <BookMarked size={18} style={{ color: 'var(--primary)' }} />
              <span className="text-xs font-mono uppercase tracking-widest" style={{ color: 'var(--primary)', fontFamily: 'var(--font-mono)' }}>
                Published Archives
              </span>
            </div>
            <h2
              className="text-2xl sm:text-3xl font-bold leading-tight"
              style={{ color: 'var(--foreground)', fontFamily: 'var(--font-serif, serif)' }}
            >
              Latest Published Archives
            </h2>
            <p className="mt-2 text-sm max-w-xl" style={{ color: 'var(--muted-foreground)' }}>
              Curated collections of historical records, oral traditions, and sourced accounts — open for public exploration.
            </p>
          </div>
          <Link
            href="/archives"
            className="hidden sm:flex items-center gap-1.5 text-sm font-medium transition-colors duration-150 shrink-0"
            style={{ color: 'var(--primary)' }}
          >
            Browse all
            <ArrowRight size={14} />
          </Link>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={28} className="animate-spin" style={{ color: 'var(--muted-foreground)' }} />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {archives.map((archive) => (
              <ArchiveCard key={archive.id} archive={archive} />
            ))}
          </div>
        )}

        {/* Mobile browse all */}
        <div className="mt-8 flex sm:hidden justify-center">
          <Link
            href="/archives"
            className="flex items-center gap-1.5 text-sm font-medium"
            style={{ color: 'var(--primary)' }}
          >
            Browse all archives
            <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}
